import DropdownCtaController from "./dropdown_cta_controller.js"

// Reuses the shared top-layer popover and positioning. Exact leaf values stay
// native radios in their original form; opening/browsing never emits a change.
export default class extends DropdownCtaController {
  static targets = ["input", "summary", "list"]
  static values = { placeholder: String }

  connect() {
    super.connect()
    this.track = this.element.closest(".segmented-radio")
    this.form = this.element.closest("form")
    this.onChange = () => { this.sync(); this.close() }
    this.onReset = () => {
      this.close(false)
      cancelAnimationFrame(this.resetFrame)
      this.resetFrame = requestAnimationFrame(() => this.sync())
    }
    this.onCache = () => this.close(false)
    this.onViewportChange = event => {
      // Scrolling the options must not dismiss their own popover.
      if (event.type === "resize" || !this.menuTarget.contains(event.target)) this.close(false)
    }
    this.track.addEventListener("change", this.onChange)
    this.form?.addEventListener("reset", this.onReset)
    document.addEventListener("turbo:before-cache", this.onCache)
    this.sync()
  }

  disconnect() {
    this.close(false)
    document.removeEventListener("keydown", this._onKeydown)
    cancelAnimationFrame(this.resetFrame)
    clearTimeout(this.leaveTimer)
    this.track.removeEventListener("change", this.onChange)
    this.form?.removeEventListener("reset", this.onReset)
    document.removeEventListener("turbo:before-cache", this.onCache)
    this.removeViewportListeners()
    super.disconnect()
  }

  toggle(event) {
    event.preventDefault() // Let the shared controller position and toggle it.
    if (this.triggerTarget.matches(":disabled")) return
    super.toggle(event)
  }

  open() {
    if (this.triggerTarget.matches(":disabled") || this._isOpen()) return
    super.open()
    window.addEventListener("resize", this.onViewportChange)
    document.addEventListener("scroll", this.onViewportChange, true)
    const input = this.inputTargets.find(input => input.checked) || this.inputTargets[0]
    this.focusInput(input)
  }

  _positionMenu() {
    this.menuTarget.style.width = `${Math.min(this.track.offsetWidth, window.innerWidth - 24)}px`
    const rect = this.triggerTarget.getBoundingClientRect()
    const room = Math.max(rect.top - 12, window.innerHeight - rect.bottom - 12)
    this.listTarget.style.maxHeight = `${Math.max(44, Math.min(168, room - 64))}px`
    super._positionMenu()
  }

  close(restoreFocus = true) {
    if (!this._isOpen()) return
    this.menuTarget.hidePopover()
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this.chevronTarget.style.transform = ""
    document.removeEventListener("keydown", this._onKeydown)
    this.removeViewportListeners()
    if (restoreFocus) this.triggerTarget.focus({ preventScroll: true })
  }

  _handleToggle(event) {
    super._handleToggle(event)
    if (event.newState === "closed") this.removeViewportListeners()
  }

  removeViewportListeners() {
    window.removeEventListener("resize", this.onViewportChange)
    document.removeEventListener("scroll", this.onViewportChange, true)
  }

  openWithKeyboard(event) {
    if (!["ArrowDown", "ArrowUp"].includes(event.key)) return
    event.preventDefault()
    event.stopPropagation()
    this.open()
    if (event.key === "ArrowUp") this.focusInput(this.inputTargets.at(-1))
  }

  _handleKeydown(event) {
    if (!this.element.contains(event.target)) return
    const inputs = this.inputTargets.filter(input => !input.matches(":disabled"))
    const index = inputs.indexOf(document.activeElement)
    let next
    switch (event.key) {
      case "ArrowDown": case "ArrowRight": next = (index + 1) % inputs.length; break
      case "ArrowUp": case "ArrowLeft": next = (index - 1 + inputs.length) % inputs.length; break
      case "Home": next = 0; break
      case "End": next = inputs.length - 1; break
      case "Escape": event.preventDefault(); this.close(); return
      case "Enter":
        if (index >= 0) { event.preventDefault(); inputs[index].click() }
        return
      default: return // Space uses the native radio; Tab can leave the dialog.
    }
    event.preventDefault()
    this.focusInput(inputs[next])
  }

  focusInput(input) {
    input?.focus({ preventScroll: true })
    if (!input) return
    // scrollIntoView also scrolls the page for top-layer elements in Chrome.
    // Move only the list so keyboard browsing cannot move the card/dismiss it.
    const row = input.closest("label").getBoundingClientRect()
    const list = this.listTarget.getBoundingClientRect()
    if (row.top < list.top) this.listTarget.scrollTop += row.top - list.top
    else if (row.bottom > list.bottom) this.listTarget.scrollTop += row.bottom - list.bottom
  }

  leave(event) {
    clearTimeout(this.leaveTimer)
    const check = target => { if (!this.element.contains(target)) this.close(false) }
    if (event.relatedTarget) check(event.relatedTarget)
    else this.leaveTimer = setTimeout(() => check(document.activeElement), 0)
  }

  sync() {
    const input = this.inputTargets.find(input => input.checked)
    this.summaryTarget.textContent = input?.dataset.summary || this.placeholderValue
    const label = input ? `${this.placeholderValue}: ${input.dataset.label}` : this.placeholderValue
    this.triggerTarget.setAttribute("aria-label", label)
    this.triggerTarget.title = label
  }
}
